"use client";

import React, { useRef, useState, useMemo, useCallback } from "react";
import { Check, Loader2, Square } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import {
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
  SEARCHABLE_DROPDOWN_OPTION_CLASS,
  SEARCHABLE_DROPDOWN_SEARCH_INPUT_CLASS,
  SEARCHABLE_DROPDOWN_SEARCH_SHELL_CLASS,
} from "@/lib/dropdown-styles";
import { SEARCHABLE_THRESHOLD } from "@/lib/dropdown-constants";
import type { SearchableOption } from "@/lib/dropdown-types";

const OPTION_HEIGHT = 44;

export interface SearchablePickerListProps {
  options: SearchableOption[];
  label: string;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (option: SearchableOption) => void;
  showFlag?: boolean;
  showCode?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  searchPlaceholder?: string;
  optionClassName?: string;
  searchInputClassName?: string;
  clientFilter?: boolean;
  selectedCode?: string | null;
  multiSelect?: boolean;
  selectedCodes?: string[];
  onToggleSelect?: (option: SearchableOption) => void;
  onBulkChange?: (codes: string[]) => void;
  showBulkActions?: boolean;
  selectAllLabel?: string;
  deselectAllLabel?: string;
  selectAllExcludeCodes?: string[];
  listClassName?: string;
  listStyle?: React.CSSProperties;
  autoFocusSearch?: boolean;
  variant?: "dropdown" | "sheet";
}

function filterOptions(options: SearchableOption[], label: string, query: string): SearchableOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((option) => {
    const name = String(option[label] ?? option.name ?? "").toLowerCase();
    const code = String(option.code ?? "").toLowerCase();
    return name.includes(q) || code.includes(q);
  });
}

function OptionRow({
  option,
  label,
  showFlag,
  showCode,
  optionClassName,
  selected,
  multiSelect,
  onSelect,
}: {
  option: SearchableOption;
  label: string;
  showFlag?: boolean;
  showCode?: boolean;
  optionClassName?: string;
  selected?: boolean;
  multiSelect?: boolean;
  onSelect: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const flagValue = option.flag;
  const isEmojiFlag = typeof flagValue === "string" && flagValue.length <= 4 && !flagValue.startsWith("http");

  return (
    <div
      role="option"
      aria-selected={selected}
      data-pressed={pressed || undefined}
      className={cn(
        optionClassName,
        !optionClassName && cn(SEARCHABLE_DROPDOWN_OPTION_CLASS, selected && !pressed && "bg-accent dark:bg-neutral-700"),
      )}
      onPointerDown={(event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        setPressed(true);
      }}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onClick={onSelect}
    >
      {multiSelect ? (
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
            selected ? "border-white bg-white text-neutral-900" : "border-neutral-500 bg-transparent",
          )}
          aria-hidden
        >
          {selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
        </span>
      ) : null}
      {showFlag && flagValue ? (
        isEmojiFlag ? (
          <span className="text-lg shrink-0">{flagValue}</span>
        ) : (
          <img src={flagValue || "/placeholder.svg"} alt="" className="w-5 h-5 shrink-0" />
        )
      ) : null}
      <span className="truncate">
        {String(option[label] ?? option.name ?? "")}
        {showCode && (option.trailing ?? option.dial ?? option.code)
          ? ` (${String(option.trailing ?? option.dial ?? option.code)})`
          : ""}
      </span>
    </div>
  );
}

export function SearchablePickerList({
  options,
  label,
  query,
  onQueryChange,
  onSelect,
  showFlag = true,
  showCode = false,
  loading = false,
  emptyMessage = "No results found",
  searchPlaceholder = "Search...",
  optionClassName,
  searchInputClassName,
  clientFilter = true,
  selectedCode,
  multiSelect = false,
  selectedCodes = [],
  onToggleSelect,
  onBulkChange,
  showBulkActions,
  selectAllLabel = "Select all",
  deselectAllLabel = "Deselect all",
  selectAllExcludeCodes = [],
  listClassName,
  listStyle,
  autoFocusSearch = true,
  variant = "dropdown",
}: SearchablePickerListProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const visibleOptions = clientFilter ? filterOptions(options, label, query) : options;
  const shouldVirtualize = visibleOptions.length >= SEARCHABLE_THRESHOLD;
  const excludeSet = useMemo(() => new Set(selectAllExcludeCodes), [selectAllExcludeCodes]);

  const selectableVisibleOptions = useMemo(
    () => visibleOptions.filter((option) => !excludeSet.has(option.code)),
    [visibleOptions, excludeSet],
  );

  const bulkActionsVisible =
    multiSelect &&
    (showBulkActions ?? options.length >= SEARCHABLE_THRESHOLD) &&
    selectableVisibleOptions.length > 0 &&
    Boolean(onBulkChange || onToggleSelect);

  const allVisibleSelected =
    bulkActionsVisible &&
    selectableVisibleOptions.every((option) => selectedCodes.includes(option.code));

  const handleBulkToggle = useCallback(() => {
    if (onBulkChange) {
      const visibleCodeSet = new Set(selectableVisibleOptions.map((option) => option.code));
      if (allVisibleSelected) {
        onBulkChange(selectedCodes.filter((code) => !visibleCodeSet.has(code)));
      } else {
        onBulkChange([
          ...Array.from(
            new Set([
              ...selectedCodes,
              ...selectableVisibleOptions.map((option) => option.code),
            ]),
          ),
        ]);
      }
      return;
    }
    if (!onToggleSelect) return;
    if (allVisibleSelected) {
      const visibleCodeSet = new Set(selectableVisibleOptions.map((option) => option.code));
      selectedCodes
        .filter((code) => visibleCodeSet.has(code))
        .forEach((code) => {
          const option = visibleOptions.find((item) => item.code === code);
          if (option) onToggleSelect(option);
        });
      return;
    }
    selectableVisibleOptions
      .filter((option) => !selectedCodes.includes(option.code))
      .forEach((option) => onToggleSelect(option));
  }, [
    allVisibleSelected,
    onBulkChange,
    onToggleSelect,
    selectableVisibleOptions,
    selectedCodes,
    visibleOptions,
  ]);

  const resolveSelected = useCallback(
    (option: SearchableOption) =>
      multiSelect ? selectedCodes.includes(option.code) : selectedCode != null && option.code === selectedCode,
    [multiSelect, selectedCodes, selectedCode],
  );

  const handleOptionPress = useCallback(
    (option: SearchableOption) => {
      if (multiSelect && onToggleSelect) {
        onToggleSelect(option);
        return;
      }
      onSelect(option);
    },
    [multiSelect, onToggleSelect, onSelect],
  );

  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getItemKey: (index) => visibleOptions[index]?.isoCode ?? visibleOptions[index]?.code ?? index,
    getScrollElement: () => listRef.current,
    estimateSize: () => OPTION_HEIGHT,
    overscan: 12,
  });

  React.useEffect(() => {
    if (autoFocusSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [autoFocusSearch]);

  return (
    <div className={cn("flex min-h-0 flex-col", variant === "sheet" && "h-full flex-1")}>
      <div
        className={cn(
          "sticky top-0 z-20 shrink-0 isolate bg-transparent px-2",
          variant === "sheet" ? "pt-0 pb-2" : "pt-2 pb-1",
        )}
      >
        <div className={cn("relative z-10 overflow-hidden", SEARCHABLE_DROPDOWN_SEARCH_SHELL_CLASS)}>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              "relative z-10",
              SEARCHABLE_DROPDOWN_SEARCH_INPUT_CLASS,
              searchInputClassName,
            )}
            style={{ fontSize: 16 }}
            inputMode="search"
            autoComplete="off"
          />
        </div>
      </div>

      {bulkActionsVisible ? (
        <div className="flex shrink-0 justify-end px-2 pb-1">
          <button
            type="button"
            onClick={handleBulkToggle}
            className={cn(
              "inline-flex items-center gap-1.5 text-sm underline-offset-2 hover:underline",
              SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
            )}
          >
            {allVisibleSelected ? (
              <>
                <Check className="h-3.5 w-3.5" />
                {deselectAllLabel}
              </>
            ) : (
              <>
                <Square className="h-3.5 w-3.5" />
                {selectAllLabel}
              </>
            )}
          </button>
        </div>
      ) : null}

      <div
        ref={listRef}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto scrollbar-hide px-2",
          variant === "sheet"
            ? "pb-[max(0.5rem,env(safe-area-inset-bottom))]"
            : "pb-2",
          listClassName,
        )}
        style={listStyle}
        role="listbox"
      >
        {loading ? (
          <div className={cn("flex flex-col items-center justify-center gap-2 py-8", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : visibleOptions.length === 0 ? (
          <div className={cn("text-base text-center py-2", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>
            {emptyMessage}
          </div>
        ) : shouldVirtualize ? (
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative", width: "100%" }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const option = visibleOptions[virtualRow.index];
              if (!option) return null;
              const key = option.isoCode || `${option.code}-${option.name}`;
              return (
                <div
                  key={key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <OptionRow
                    option={option}
                    label={label}
                    showFlag={showFlag}
                    showCode={showCode}
                    optionClassName={optionClassName}
                    selected={resolveSelected(option)}
                    multiSelect={multiSelect}
                    onSelect={() => handleOptionPress(option)}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          visibleOptions.map((option) => {
            const key = option.isoCode || `${option.code}-${option.name}`;
            return (
              <OptionRow
                key={key}
                option={option}
                label={label}
                showFlag={showFlag}
                showCode={showCode}
                optionClassName={optionClassName}
                selected={resolveSelected(option)}
                multiSelect={multiSelect}
                onSelect={() => handleOptionPress(option)}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
